import React, {
  ComponentState,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from "react";

import * as api from "api/datatable";

import {
  ColumnMovedEvent,
  ColumnResizedEvent,
  RowClickedEvent,
  ServerSideTransaction,
  ValueSetterParams
} from "ag-grid-community";
import AgGridContainer, { flatDataFromPopjo, WviState } from "./AgGridContainer";
import {
  DTReducer,
  fetchData,
  getRSQLFilter,
  initialDTState,
  loadDatatableDefinition
} from "./ReadOnlyTable";
import { Pojo, WhenValidateItemResult } from "types/Galaxy";
import toaster from "composants/toaster/toaster";
import { AgGridReact } from "ag-grid-react";
import produce from "immer";
import { useDispatch } from "react-redux";
import { contextualize } from "actions/contextTreeModule";
import { FilterModel, SortModelItem } from "ag-grid-community";
import { sortModelToOrderBy } from "./utils";

interface DatatableProps {
  sjmoCode: string;
  tableName: string;
  ctrlkey: string;
  nodeType: string;
  processIdDrop: string;
  onAfterSaveDatatable: () => void;
  onAfterDeleteDatatable: () => void;
  onRowClickParent: (id: string) => void;
}

/**
 * Retourne la liste des colonnes qui ont wvi = true
 * @param columns Liste des colonnes de la définition
 * @returns Liste des colonnes avec wvi = true
 */
const getWviColumns = (columns: ComponentState[]): ComponentState[] => {
  return columns.filter(column => column.wvi === true).map(column => column.column);
};

const Datatable: FC<DatatableProps> = ({
  sjmoCode,
  tableName,
  ctrlkey,
  nodeType,
  processIdDrop,
  onAfterSaveDatatable,
  onAfterDeleteDatatable,
  onRowClickParent
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const reduxDispatch = useDispatch();
  const [definition, dispatchDefinition] = useReducer(DTReducer, initialDTState);
  const [dirtyRows, setDirtyRows] = useState<Record<string, Partial<Pojo>>>({});

  const currentFocus = useMemo(
    () => definition.focus.find(f => f.focusId === definition.selectedFocus),
    [definition.focus, definition.selectedFocus]
  );

  const [wviState, setWviState] = useState<WviState>({});

  useEffect(() => {
    if (definition.focus.length === 0) {
      loadDatatableDefinition(sjmoCode, tableName, ctrlkey).then(([focus, columns]) => {
        dispatchDefinition({
          type: "FETCH_DEFINITIONS",
          payload: { focus, columns, selectedFocus: focus[0].focusId }
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sjmoCode, ctrlkey, tableName]);

  const onFocusChange = useCallback(
    async (selected: string) => {
      const columnRes = await api.getColumn(sjmoCode, selected);

      dispatchDefinition({
        type: "CHANGE_FOCUS",
        payload: {
          selectedFocus: selected,
          columns: columnRes.data
        }
      });
    },
    [sjmoCode]
  );

  /**
   * Gestion du déplacement des colonnes
   * L'évènement est envoyé pluseieurs fois lors du drag and drop.
   * On n'enregistre le déplacement qu'au moment où l'utilisateur lache la colonne IE l'index est le même que le précédent.
   */
  const movingColumnIndexRef = useRef<number | null>(null);
  const onColumnMoved = useCallback(
    (event: ColumnMovedEvent) => {
      const columnField = event.column?.getColId();

      // +1 pour la selection et +1 pour les satellites
      const newIndex = (event.toIndex as number) - 2;
      if (columnField && currentFocus && movingColumnIndexRef.current !== newIndex) {
        movingColumnIndexRef.current = newIndex;
      } else if (columnField && currentFocus && movingColumnIndexRef.current === newIndex) {
        // Récupérer la colonne déplacée et son ancien index
        const movedColumn = definition.columns.find(c => c.column === columnField);
        const oldIndex = definition.columns.findIndex(c => c.column === columnField);

        if (movedColumn && oldIndex !== -1) {
          // Créer une copie du tableau des colonnes
          const updatedColumns = [...definition.columns];

          // Retirer la colonne de son ancien index
          updatedColumns.splice(oldIndex, 1);

          // Insérer la colonne à son nouvel index
          updatedColumns.splice(newIndex, 0, movedColumn);

          // Dispatcher la nouvelle définition
          dispatchDefinition({
            type: "CHANGE_FOCUS",
            payload: {
              selectedFocus: currentFocus.focusId,
              columns: updatedColumns
            }
          });
        }
      }
    },
    [currentFocus, definition.columns]
  );

  const onColumnResized = useCallback(
    (params: ColumnResizedEvent) => {
      const columnName = params.column?.getColId();
      const width = params.column?.getActualWidth();

      if (!columnName || !width || !params.finished) return;

      if (currentFocus) {
        const updatedColumns = produce(definition.columns, draft => {
          const column = draft.find(c => c.column === columnName);
          if (column) {
            column.contentSize = width;
            column.labelSize = width;
            console.log("log temporaire", column.column, width);
          }
        });

        dispatchDefinition({
          type: "CHANGE_FOCUS",
          payload: {
            selectedFocus: currentFocus?.focusId,
            columns: updatedColumns
          }
        });
      }
    },
    [currentFocus, definition.columns]
  );

  const onSaveFocusPerso = useCallback(
    async ({
      currentFilter,
      currentOrderBy
    }: {
      currentFilter?: FilterModel;
      currentOrderBy?: SortModelItem[];
    }) => {
      if (currentFocus) {
        try {
          const orderBy = sortModelToOrderBy(currentOrderBy ?? []);
          await api.saveFocusPersoDatatable(
            currentFocus.focusId,
            tableName,
            ctrlkey,
            sjmoCode,
            definition.columns,
            [],
            orderBy || undefined
          );
          if (sessionStorage.getItem("superUser") !== "true") {
            const [newFocus, newColumns] = await loadDatatableDefinition(
              sjmoCode,
              tableName,
              ctrlkey
            );
            dispatchDefinition({
              type: "FETCH_DEFINITIONS",
              payload: { focus: newFocus, columns: newColumns, selectedFocus: newFocus[0].focusId }
            });
          }
        } catch (error) {
          console.error(
            "error during the save of the modified focus ",
            currentFocus.focusCode,
            " of the datatable ",
            ctrlkey
          );
          console.error(error);
        }
      }
    },
    [currentFocus, definition.columns, ctrlkey, sjmoCode, tableName]
  );

  const onRazFocusPerso = useCallback(async () => {
    if (currentFocus) {
      await api.razFocusPersoDatatable(currentFocus.focusId, sjmoCode);
      const [newFocus, newColumns] = await loadDatatableDefinition(sjmoCode, tableName, ctrlkey);
      dispatchDefinition({
        type: "FETCH_DEFINITIONS",
        payload: { focus: newFocus, columns: newColumns, selectedFocus: newFocus[0].focusId }
      });
    }
  }, [ctrlkey, currentFocus, sjmoCode, tableName]);

  const onRowClickFunction = useCallback(
    (event: RowClickedEvent) => {
      if (onRowClickParent) {
        onRowClickParent(event.data.id);
      } else {
        reduxDispatch(contextualize(sjmoCode, tableName, ctrlkey, event.data));
      }
    },
    [onRowClickParent, reduxDispatch, sjmoCode, tableName, ctrlkey]
  );

  const saveRows = useCallback(
    async (rows: Partial<Pojo>[]) => {
      await api.createMany(tableName, rows as Pojo[], sjmoCode);
    },
    [tableName, sjmoCode]
  );

  const deleteRows = useCallback(
    async (rows: Partial<Pojo>[]) => {
      await api.deleteMany(
        tableName,
        rows.map(r => r.id as string),
        sjmoCode
      );
    },
    [tableName, sjmoCode]
  );

  const wvi = useCallback(
    function wvi(pojo: Partial<Pojo>, field: string, index: number) {
      // TODO : on bloque toute tentative de sauvegarde.
      // dispatch({ type: "CHANGE_WVI_PENDING" });
      const flatData = flatDataFromPopjo(pojo);
      if (pojo) {
        setTimeout(() => {
          api
            .whenValidateItem(
              sjmoCode,
              tableName,
              field,
              flatData as Pojo,
              definition.columns.filter(c => c.typeCompo === "OEL").map(c => c.column)
            )
            .then(res => {
              setDirtyRows(list => ({ ...list, [index]: res.data.entity }));
              const transaction: ServerSideTransaction = {
                update: [{ ...res.data.entity, current: pojo }]
              };
              const result = gridRef.current!.api.applyServerSideTransaction(transaction);
              console.log("result", result);
              return res.data;
            })
            .catch(reason => {
              return reason.response.data as WhenValidateItemResult<Pojo>;
            })
            .then(wvi => {
              setWviState(list => ({
                ...list,
                [index]: {
                  [field]: { type: wvi.message.type, message: wvi.message.message }
                }
              }));
              if (wvi.message.message && wvi.message.type === "DANGER") {
                toaster.error(wvi.message.message);
              } else if (wvi.message.message && wvi.message.type === "WARNING") {
                toaster.info(wvi.message.message);
              }
            })
            .catch(e => {
              console.error("An error occured when handling the wvi message : ", e);
            });
        }, 0);
      }
    },
    [definition.columns, sjmoCode, tableName]
  );

  const onChange = useCallback(
    (params: ValueSetterParams<Pojo, string>) => {
      // Modifier la valeur visible dans la cellule
      params.data[params.colDef.field as string] = params.newValue;

      if (getWviColumns(definition.columns).includes(params.colDef.field)) {
        wvi(params.data, params.colDef.field as string, params.node?.rowIndex ?? 0);
      } else {
        // Créer une copie de l'objet en ne gardant que les types simples afin de pouvoir l'envoyer au back
        const flatData = flatDataFromPopjo(params.data);
        setDirtyRows(list => ({ ...list, [params.data.id]: flatData }));
      }
      return true;
    },
    [definition.columns, wvi]
  );

  return (
    <WviContext.Provider value={{ wviState }}>
      <AgGridContainer
        ref={gridRef}
        sjmoCode={sjmoCode}
        tableName={tableName}
        ctrlkey={ctrlkey}
        definition={definition}
        currentFocus={currentFocus}
        nodeType={nodeType}
        processIdDrop={processIdDrop}
        dirtyRows={dirtyRows}
        wviState={wviState}
        insertable={true}
        onChange={onChange}
        saveRows={saveRows}
        deleteRows={deleteRows}
        onAfterDeleteDatatable={onAfterDeleteDatatable}
        onAfterSaveDatatable={onAfterSaveDatatable}
        getRSQLFilter={getRSQLFilter}
        fetchData={fetchData}
        onSaveFocusPerso={onSaveFocusPerso}
        onRazFocusPerso={onRazFocusPerso}
        onFocusChange={onFocusChange}
        onColumnMoved={onColumnMoved}
        onRowClick={onRowClickFunction}
        setDirtyRows={setDirtyRows}
        onColumnResized={onColumnResized}
      />
    </WviContext.Provider>
  );
};

export const WviContext = React.createContext<{
  wviState: WviState;
}>({
  wviState: {}
});

export default Datatable;
